import { useDispatch, useSelector } from 'react-redux';
import { useOutboundContext } from './outbound-provider';
import { OutboundSessionStates } from './OutboundSession';
import { useAppSelector } from 'shared/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addCallTasks,
  resetCallTasks,
  selectFilteredCallTasks,
  setApolloCallDispositions,
  setApolloContactLists,
  setApproximateTaskCount,
  setCallCount,
  setCallTasks,
  setContactListQuery,
  setContactListsLoaded,
  setCursor,
  setDetailOpen,
  setDetailTask,
  setDialSession,
  setHubspotCallDispositions,
  setHubspotContactLists,
  setIsLoadingTargets,
  setIsResettingSource,
  setOnboardingStatus,
  setOutreachCallDispositions,
  setOutreachContactLists,
  setProspectProperties,
  setSalesforceCallDispositions,
  setSalesforceContactLists,
  setSalesloftCallDispositions,
  setSalesloftCallSentiments,
  setSalesloftContactLists,
  setServicebellCallDispositions,
  setServicebellContactLists,
  setSessionPrepared,
  setSourceId,
  setSourceType,
  setStatus,
  updateCallTask,
} from 'shared/store/outbound-slice';
import {
  CallTask,
  CallTaskStatus,
  DialSession,
  LIST_SOURCE_TYPES,
  ProspectProperty,
  SourceType,
} from 'types/outbound';
import {
  deleteServicebellContactList,
  getApolloCallDispositions,
  getApolloCallTasks,
  getApolloContactListCallTasks,
  getApolloContactLists,
  getDebugCallTasks,
  getHubspotCallDispositions,
  getHubspotCallTasks,
  getHubspotContactListCallTasks,
  getHubspotContactLists,
  getOutboundOnboardingStatus,
  getOutreachCallDispositions,
  getOutreachCallTasks,
  getOutreachContactListCallTasks,
  getOutreachContactLists,
  getSalesforceCallDispositions,
  getSalesforceContactListCallTasks,
  getSalesforceContactLists,
  getSalesforceContactListSchema,
  getSalesloftCallDispositions,
  getSalesloftCallTasks,
  getSalesloftContactListCallTasks,
  getSalesloftContactLists,
  getServicebellCallDispositions,
  getServicebellContactLists,
  getServicebellContactListCallTasks,
  resetHubspotContactListCallTasks,
  resetOutreachContactListCallTasks,
  resetSalesforceContactListCallTasks,
  getSalesloftCallSentiments,
  resetServicebellContactListCallTasks,
} from 'shared/api';
import { message } from 'antd';
import { makeLog } from 'shared/utils/log';
import { loadOrgOutboundSettings } from 'shared/store/org-slice';
import { getSequencerExternalContactUrl } from 'browser/components/Outbound/helpers';
import { selectActiveOrgIntegrations } from 'shared/store/auth-slice';
import {
  setTranscription,
  setTranscriptionMessage,
} from 'shared/store/transcription-slice';
import { useUpdateUserOutboundSettings } from 'browser/components/Settings/Dialer/useUpdateUserOutboundSettings';
import { LBBContactPhoneNumber } from 'sb-lib/types';

const log = makeLog('useOutbound');

export const DEFAULT_PROSPECT_PROPERTIES: ProspectProperty[] = [
  {
    propertyKey: 'firstName',
    displayName: 'First Name',
    visible: true,
    readOnly: false,
    type: 'string',
  },
  {
    propertyKey: 'lastName',
    displayName: 'Last Name',
    visible: true,
    readOnly: false,
    type: 'string',
  },
  {
    propertyKey: 'title',
    displayName: 'Title',
    visible: true,
    readOnly: false,
    type: 'string',
  },
  {
    propertyKey: 'phone',
    displayName: 'Phone Number',
    visible: true,
    readOnly: false,
    type: 'string',
  },
  {
    propertyKey: 'email',
    displayName: 'Email',
    visible: true,
    readOnly: false,
    type: 'string',
  },
];

// DO NOT put effects in this hook, at least not without first seriously
// considering whether or not you _have to_. It gets used all over the
// place, meaning it gets re-evaluated very often. Any effect that opens
// resources will likely end up leaking those resources. If you need
// an effect to get run for an outbound session, put it in either the
// Outbound compmonent or the OutboundActiveCall component.
export const useOutbound = (runEffects = false) => {
  const dispatch = useDispatch();
  const { session, transcriber, pristineMp3 } = useOutboundContext();
  const [loadedTwilioAccountStatus, setLoadedTwilioAccountStatus] = useState(false);
  const {
    amdState,
    callCount,
    cursor,
    enforceDNC,
    muted,
    onboardingStatus,
    preRecordedVoicemailId,
    selectedNumber,
    sessionPrepared,
    sourceId,
    sourceType,
    status,
  } = useAppSelector(s => s.outbound);
  const filteredCallTasks = useAppSelector(selectFilteredCallTasks);
  const orgIntegrations = useSelector(selectActiveOrgIntegrations);
  const { preferredAudioDeviceId } = useAppSelector(s => s.dashboard);
  const { settings } = useUpdateUserOutboundSettings();

  const prepareSession = useCallback(() => {
    if (sessionPrepared) {
      log.warn("prepareSession: called, but there's already a session");
      return;
    }

    function openSequencerProfile(task: CallTask) {
      const sequencerUrl = getSequencerExternalContactUrl(task, orgIntegrations);
      if (!sequencerUrl) {
        log.warn('openSequencerProfile: no sequencer URL');
        return;
      }
      log.debug('Opening sequencer profile', { sequencerUrl });
      window.open(sequencerUrl, 'prospect-profile');
    }

    const currentSession = session.current;
    const currentTranscriber = transcriber.current;
    currentSession.removeAllListeners();

    currentSession.on('task.changed', (task: CallTask, oldTask: CallTask) => {
      dispatch(updateCallTask(task));

      if (!settings?.outboundAutoOpenSequencerProfiles) {
        return;
      }

      if (
        (callCount === 1 &&
          task.status === CallTaskStatus.RINGING &&
          oldTask.status !== CallTaskStatus.RINGING) ||
        (callCount > 1 &&
          task.status === CallTaskStatus.ANSWERED &&
          oldTask.status !== CallTaskStatus.ANSWERED)
      ) {
        openSequencerProfile(task);
      }
    });

    currentSession.on('session.created', (dialSession: DialSession) => {
      dispatch(setDialSession(dialSession));
      currentTranscriber.connect(dialSession.id);
    });

    currentSession.on('session.end', () => {
      dispatch(resetCallTasks());
      currentTranscriber.disconnect();
    });

    currentSession.on('session.status', async (s: OutboundSessionStates) => {
      dispatch(setStatus(s));
    });

    currentSession.on('call.connected', async (task: CallTask | undefined) => {
      if (!task || !task.id) {
        // The task may be undefined after a reconnect.
        log.debug('Ignoring call connected w/o task.');
        return;
      }
      if (task.shouldRecord) {
        await currentTranscriber.switchDialTask(currentSession, task.id);
      }
      dispatch(setDetailTask(task));
      if (pristineMp3.current) {
        const audio = pristineMp3.current;
        currentSession.muteOutgoingAudio(true);
        await audio.play();
        setTimeout(() => {
          audio.pause();
          audio.currentTime = 0;
          currentSession.muteOutgoingAudio(false);
        }, 500);
      }
    });

    currentSession.on('call.end', async (task: CallTask | undefined) => {
      if (task?.shouldRecord) {
        currentTranscriber.endTranscription();
      }
    });

    currentTranscriber.on('message', async ({ message }) => {
      if (message.isPartial) {
        dispatch(setTranscriptionMessage(message));
      } else {
        dispatch(setTranscription(message));
      }
    });

    log.debug('Session prepared.');
    dispatch(setSessionPrepared(true));
  }, [
    callCount,
    dispatch,
    orgIntegrations,
    pristineMp3,
    session,
    sessionPrepared,
    settings?.outboundAutoOpenSequencerProfiles,
    transcriber,
  ]);

  const inputDevice = useMemo(() => preferredAudioDeviceId ?? 'default', [
    preferredAudioDeviceId,
  ]);

  const loadMoreData = useCallback(async () => {
    return await makeLoadMoreData(dispatch, cursor, sourceId, sourceType)();
  }, [cursor, dispatch, sourceId, sourceType]);

  const getCallTargets = useCallback(
    async (si?: number, st?: SourceType | 'debug') => {
      dispatch(setIsLoadingTargets(true));

      const localSourceId = si || sourceId;
      const localSourceType = st || sourceType;

      dispatch(setProspectProperties(DEFAULT_PROSPECT_PROPERTIES));
      dispatch(loadOrgOutboundSettings());

      switch (localSourceType) {
        case 'debug':
          try {
            const { data } = await getDebugCallTasks();
            dispatch(setCallTasks(data));
            dispatch(setApproximateTaskCount(null));
            dispatch(setSourceType(SourceType.CONTACT));
          } catch {
            dispatch(setCallTasks([]));
            dispatch(setApproximateTaskCount(null));
          }
          break;
        case SourceType.APOLLO_TASK:
          try {
            const response = await getApolloCallTasks();
            dispatch(setCallTasks(response.data));
            dispatch(setApproximateTaskCount(null));
          } catch (err) {
            dispatch(setCallTasks([]));
            dispatch(setApproximateTaskCount(null));
            log.error('Error retrieving Apollo Call Tasks: ', err);
          }
          break;
        case SourceType.APOLLO_SEQUENCE:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                id: number,
                cursor = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const response = await getApolloContactListCallTasks(id, cursor);

                const newAccumulatedTasks = [
                  ...accumulatedTasks,
                  ...response.data.callTasks,
                ];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(response.data.approximateCount));
                dispatch(setCursor(response.data.cursor));

                const anyPending = response.data.callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (response.data.cursor) {
                  await fetchTasks(id, response.data.cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              log.error('Error retrieving Apollo Contact Lists: ', err);
            }
          }
          break;
        case SourceType.HUBSPOT_TASK:
          try {
            const response = await getHubspotCallTasks();
            dispatch(setCallTasks(response.data));
            dispatch(setApproximateTaskCount(null));
          } catch (err) {
            dispatch(setCallTasks([]));
            dispatch(setApproximateTaskCount(null));
            log.error('Error retrieving HubSpot Call Tasks: ', err);
          }
          break;
        case SourceType.HUBSPOT_LIST:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                listId: number,
                cursor = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const response = await getHubspotContactListCallTasks(listId, cursor);
                const newAccumulatedTasks = [
                  ...accumulatedTasks,
                  ...response.data.callTasks,
                ];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(response.data.approximateCount));
                dispatch(setCursor(response.data.cursor));

                const anyPending = response.data.callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (response.data.cursor) {
                  await fetchTasks(listId, response.data.cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              log.error(
                `Error retrieving Call Tasks for Contact List ${localSourceId}`,
                err,
              );
            }
          }
          break;
        case SourceType.OUTREACH_TASK:
          try {
            const response = await getOutreachCallTasks();

            dispatch(setCallTasks(response.data));
            dispatch(setApproximateTaskCount(null));
          } catch (err) {
            dispatch(setCallTasks([]));
            dispatch(setApproximateTaskCount(null));
            log.error('Error retrieving Outreach Call Tasks: ', err);
          }
          break;
        case SourceType.OUTREACH_SEQUENCE:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                id: number,
                cursor = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const response = await getOutreachContactListCallTasks(id, cursor);

                const newAccumulatedTasks = [
                  ...accumulatedTasks,
                  ...response.data.callTasks,
                ];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(response.data.approximateCount));
                dispatch(setCursor(response.data.cursor));

                const anyPending = response.data.callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (response.data.cursor) {
                  await fetchTasks(id, response.data.cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              log.error('Error retrieving Outreach Contact Lists: ', err);
            }
          }
          break;
        case SourceType.SALESFORCE_REPORT:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                id: number,
                cr = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const [
                  {
                    data: { callTasks, approximateCount, cursor },
                  },
                  {
                    data: { prospectProperties },
                  },
                ] = await Promise.all([
                  getSalesforceContactListCallTasks(id, cr),
                  getSalesforceContactListSchema(id),
                ]);

                const newAccumulatedTasks = [...accumulatedTasks, ...callTasks];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(approximateCount));
                dispatch(setCursor(cursor));
                dispatch(setProspectProperties(prospectProperties));

                const anyPending = callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (cursor) {
                  await fetchTasks(id, cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              log.error('Error retrieving Salesforce Contact Lists: ', err);
            }
          }
          break;
        case SourceType.SALESLOFT_CADENCE:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                id: number,
                cr = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const {
                  data: { callTasks, approximateCount, cursor },
                } = await getSalesloftContactListCallTasks(localSourceId, cr);

                const newAccumulatedTasks = [...accumulatedTasks, ...callTasks];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(approximateCount));
                dispatch(setCursor(cursor));

                const anyPending = callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (cursor) {
                  await fetchTasks(id, cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              log.error('Error retrieving Salesforce Contact Lists: ', err);
            }
          }
          break;
        case SourceType.SALESLOFT_TASK:
          try {
            const { data } = await getSalesloftCallTasks();
            dispatch(setCallTasks(data));
            dispatch(setApproximateTaskCount(null));
          } catch (err) {
            dispatch(setCallTasks([]));
            dispatch(setApproximateTaskCount(null));
            log.error('Error retrieving Outreach Call Tasks: ', err);
          }
          break;
        case SourceType.SERVICEBELL_LIST:
          if (localSourceId) {
            try {
              const fetchTasks = async (
                id: number,
                cursor = undefined,
                accumulatedTasks: CallTask[] = [],
              ) => {
                const { data } = await getServicebellContactListCallTasks(id, cursor);

                const newAccumulatedTasks = [...accumulatedTasks, ...data.callTasks];

                dispatch(setCallTasks(newAccumulatedTasks));
                dispatch(setApproximateTaskCount(data.approximateCount));
                dispatch(setCursor(data.cursor));

                const anyPending = data.callTasks.some(
                  t => t.status === CallTaskStatus.PENDING,
                );

                if (anyPending) {
                  return;
                }

                if (data.cursor) {
                  await fetchTasks(id, data.cursor, newAccumulatedTasks);
                }
              };

              await fetchTasks(localSourceId);
            } catch (err) {
              dispatch(setCallTasks([]));
              dispatch(setApproximateTaskCount(null));
              log.error('Error retrieving Rep.ai Call Tasks: ', err);
            }
          }
          break;
      }
      dispatch(setIsLoadingTargets(false));
    },
    [dispatch, sourceId, sourceType],
  );

  const getDebugCallTargets = useCallback(async () => {
    await getCallTargets(undefined, 'debug');
  }, [getCallTargets]);

  const stopDialing = useCallback(async () => {
    log.assert(session.current.isOpen(), 'stopDialing: session is not open');
    await session.current.stop();
  }, [session]);

  const pauseDialing = useCallback(
    async (closeDetail = false) => {
      log.assert(session.current.isOpen(), 'pauseDialing: session is not open');
      await session.current.pause();
      if (closeDetail && session.current.status === OutboundSessionStates.paused) {
        dispatch(setDetailOpen(false));
      }
    },
    [dispatch, session],
  );

  const startDialing = useCallback(async () => {
    if (!selectedNumber || !sourceType) {
      log.debug('startDialing: not ready to start session');
      return;
    } else if (session.current.isOpen()) {
      await session.current.stop();
    }

    await session.current.start({
      preRecordedVoicemailId,
      amdState,
      inputDevice,
      loadMoreData: makeLoadMoreData(dispatch, cursor, sourceId, sourceType),
      muted,
      parallelCallCount: callCount,
      selectedNumber,
      sourceId,
      sourceType,
      stopOnCallCompleted: false,
      taskQueue: filteredCallTasks,
    });
  }, [
    preRecordedVoicemailId,
    amdState,
    callCount,
    cursor,
    dispatch,
    filteredCallTasks,
    inputDevice,
    muted,
    selectedNumber,
    session,
    sourceId,
    sourceType,
  ]);

  const startDialpadDialing = useCallback(
    async (task: CallTask) => {
      const pendingNumbers: LBBContactPhoneNumber[] = [];
      task.contact.phoneNumbers.forEach(p => {
        if (!p.isValid) {
          message.error(`Unable to dial ${p.number}: Number is invalid`);
        } else if (enforceDNC && p.doNotCall) {
          message.error(
            `Unable to dial ${p.number}: Number is on the Do Not Call registry`,
          );
        } else if (p.regionCode !== 'US') {
          message.error(
            `Unable to dial ${p.number}: Region code ${p.regionCode} is not US`,
          );
        } else {
          pendingNumbers.push(p);
        }
      });

      if (!pendingNumbers.length) {
        log.debug('startDialpadDialing: no dialable numbers');
        return;
      }

      if (!selectedNumber) {
        log.debug('startDialpadDialing: not ready to start session');
        return;
      } else if (session.current.isOpen()) {
        await session.current.stop();
      }

      dispatch(setCallTasks([task]));
      dispatch(setSourceType(SourceType.DIALPAD));
      dispatch(setSourceId(null));
      dispatch(setCallCount(1));

      await session.current.start({
        preRecordedVoicemailId,
        amdState,
        inputDevice,
        muted,
        parallelCallCount: 1,
        selectedNumber,
        sourceId: null,
        sourceType: SourceType.DIALPAD,
        stopOnCallCompleted: true,
        taskQueue: [task],
      });
    },
    [
      preRecordedVoicemailId,
      amdState,
      dispatch,
      enforceDNC,
      inputDevice,
      muted,
      selectedNumber,
      session,
    ],
  );

  const startSingleTaskDialing = useCallback(
    async (task: CallTask) => {
      if (!selectedNumber) {
        log.debug('startSingleTaskDialing: not ready to start session');
        return;
      } else if (session.current.isOpen()) {
        await session.current.stop();
      }

      await session.current.start({
        preRecordedVoicemailId,
        amdState,
        inputDevice,
        muted,
        parallelCallCount: 1,
        selectedNumber,
        sourceId,
        sourceType: sourceType || SourceType.CONTACT,
        stopOnCallCompleted: true,
        taskQueue: [{ ...task, status: CallTaskStatus.PENDING }],
      });
    },
    [
      preRecordedVoicemailId,
      amdState,
      inputDevice,
      muted,
      selectedNumber,
      session,
      sourceId,
      sourceType,
    ],
  );

  const loadOnboardingStatus = useCallback(async () => {
    try {
      const { data } = await getOutboundOnboardingStatus();
      dispatch(setOnboardingStatus(data));
      setLoadedTwilioAccountStatus(true);
    } catch (err) {
      log.error(`Error retrieving Twilio Subaccount Status`, err);
    }
  }, [dispatch]);

  const reset = useCallback(async () => {
    if (!sourceId || !LIST_SOURCE_TYPES.includes(sourceType as SourceType)) {
      return;
    } else if (session.current.isOpen()) {
      await stopDialing();
    }

    try {
      dispatch(setIsResettingSource(true));
      switch (sourceType) {
        case SourceType.HUBSPOT_LIST:
          await resetHubspotContactListCallTasks(sourceId);
          break;
        case SourceType.OUTREACH_SEQUENCE:
          await resetOutreachContactListCallTasks(sourceId);
          break;
        case SourceType.SALESFORCE_REPORT:
          await resetSalesforceContactListCallTasks(sourceId);
          break;
        case SourceType.SERVICEBELL_LIST:
          await resetServicebellContactListCallTasks(sourceId);
          break;
      }
      await getCallTargets();
    } finally {
      dispatch(setIsResettingSource(false));
    }
  }, [dispatch, getCallTargets, session, sourceId, sourceType, stopDialing]);

  const loadApolloContactLists = useCallback(
    async () =>
      getApolloContactLists().then(({ data }) => dispatch(setApolloContactLists(data))),
    [dispatch],
  );

  const loadHubspotContactLists = useCallback(
    () =>
      getHubspotContactLists().then(({ data }) => dispatch(setHubspotContactLists(data))),
    [dispatch],
  );

  const loadOutreachContactLists = useCallback(
    async () =>
      getOutreachContactLists().then(({ data }) =>
        dispatch(setOutreachContactLists(data)),
      ),
    [dispatch],
  );

  const loadSalesforceContactLists = useCallback(
    async () =>
      getSalesforceContactLists().then(({ data }) =>
        dispatch(setSalesforceContactLists(data)),
      ),
    [dispatch],
  );

  const loadSalesloftContactLists = useCallback(
    async () =>
      getSalesloftContactLists().then(({ data }) =>
        dispatch(setSalesloftContactLists(data)),
      ),
    [dispatch],
  );

  const loadServicebellContactLists = useCallback(
    async () =>
      getServicebellContactLists().then(({ data }) =>
        dispatch(setServicebellContactLists(data)),
      ),
    [dispatch],
  );

  const removeServicebellContactList = useCallback(
    async (listId: number) => {
      await deleteServicebellContactList(listId);
      await loadServicebellContactLists();
    },
    [loadServicebellContactLists],
  );

  const loadApolloCallDispositions = useCallback(async () => {
    getApolloCallDispositions().then(({ data }) =>
      dispatch(setApolloCallDispositions(data)),
    );
  }, [dispatch]);

  const loadHubspotCallDispositions = useCallback(async () => {
    getHubspotCallDispositions().then(({ data }) =>
      dispatch(setHubspotCallDispositions(data)),
    );
  }, [dispatch]);

  const loadOutreachCallDispositions = useCallback(async () => {
    getOutreachCallDispositions().then(({ data }) =>
      dispatch(setOutreachCallDispositions(data)),
    );
  }, [dispatch]);

  const loadSalesforceCallDispositions = useCallback(async () => {
    getSalesforceCallDispositions().then(({ data }) =>
      dispatch(setSalesforceCallDispositions(data)),
    );
  }, [dispatch]);

  const loadSalesloftCallDispositions = useCallback(async () => {
    getSalesloftCallDispositions().then(({ data }) =>
      dispatch(setSalesloftCallDispositions(data)),
    );
    getSalesloftCallSentiments().then(({ data }) =>
      dispatch(setSalesloftCallSentiments(data)),
    );
  }, [dispatch]);

  const loadServicebellCallDispositions = useCallback(async () => {
    getServicebellCallDispositions().then(({ data }) =>
      dispatch(setServicebellCallDispositions(data)),
    );
  }, [dispatch]);

  const loadContactLists = useCallback(async () => {
    dispatch(setContactListsLoaded(false));
    Promise.all([
      loadServicebellContactLists(),
      onboardingStatus.isApolloIntegrationSetup && loadApolloContactLists(),
      onboardingStatus.isHubspotIntegrationSetup && loadHubspotContactLists(),
      onboardingStatus.isOutreachIntegrationSetup && loadOutreachContactLists(),
      onboardingStatus.isSalesforceIntegrationSetup && loadSalesforceContactLists(),
      onboardingStatus.isSalesloftIntegrationSetup && loadSalesloftContactLists(),
    ])
      .catch(err => {
        log.error('Unable to Retrieve Contact Lists', err);
      })
      .finally(() => {
        dispatch(setContactListsLoaded(true));
      });
  }, [
    dispatch,
    loadApolloContactLists,
    loadHubspotContactLists,
    loadOutreachContactLists,
    loadSalesforceContactLists,
    loadSalesloftContactLists,
    loadServicebellContactLists,
    onboardingStatus.isApolloIntegrationSetup,
    onboardingStatus.isHubspotIntegrationSetup,
    onboardingStatus.isOutreachIntegrationSetup,
    onboardingStatus.isSalesforceIntegrationSetup,
    onboardingStatus.isSalesloftIntegrationSetup,
  ]);

  const loadCallDispositions = useCallback(async () => {
    Promise.all([
      loadServicebellCallDispositions(),
      onboardingStatus.isApolloIntegrationSetup && loadApolloCallDispositions(),
      onboardingStatus.isHubspotIntegrationSetup && loadHubspotCallDispositions(),
      onboardingStatus.isOutreachIntegrationSetup && loadOutreachCallDispositions(),
      onboardingStatus.isSalesforceIntegrationSetup && loadSalesforceCallDispositions(),
      onboardingStatus.isSalesloftIntegrationSetup && loadSalesloftCallDispositions(),
    ]).catch(err => {
      log.error('Unable to Retrieve Call Dispositions', err);
    });
  }, [
    loadApolloCallDispositions,
    loadHubspotCallDispositions,
    loadOutreachCallDispositions,
    loadSalesforceCallDispositions,
    loadSalesloftCallDispositions,
    loadServicebellCallDispositions,
    onboardingStatus.isApolloIntegrationSetup,
    onboardingStatus.isHubspotIntegrationSetup,
    onboardingStatus.isOutreachIntegrationSetup,
    onboardingStatus.isSalesforceIntegrationSetup,
    onboardingStatus.isSalesloftIntegrationSetup,
  ]);

  const togglePause = useCallback(async () => {
    if (session.current.status === OutboundSessionStates.paused) {
      await session.current.resume();
    } else {
      await session.current.pause();
    }
  }, [session]);

  const stopCall = useCallback(async () => {
    log.assert(session.current.isOpen(), 'stopCall: session is not open');
    await session.current.hangUp();
  }, [session]);

  const skipCall = useCallback(async () => {
    log.assert(session.current.isOpen(), 'skipCall: session is not open');
    await session.current.skip();
    dispatch(setDetailTask(null));
  }, [dispatch, session]);

  const toggleSkipped = useCallback(
    async (task: CallTask) => {
      const skipped = task.status !== CallTaskStatus.SKIPPED;
      dispatch(
        updateCallTask({
          ...task,
          status: skipped ? CallTaskStatus.SKIPPED : CallTaskStatus.PENDING,
        }),
      );
      session.current.markSkipped(task, skipped);
    },
    [dispatch, session],
  );

  const isOpen = useMemo(
    () =>
      status !== OutboundSessionStates.closed &&
      status !== OutboundSessionStates.initializing,
    [status],
  );
  const isPaused = useMemo(() => status === OutboundSessionStates.paused, [status]);
  const isDialing = useMemo(() => isOpen && !isPaused, [isOpen, isPaused]);
  const isConnected = useMemo(() => status === OutboundSessionStates.connected, [status]);

  const markNumberInvalid = useCallback(
    async (task: CallTask, number: string) => {
      dispatch(
        updateCallTask({
          ...task,
          invalidNumbers: [number, ...(task.invalidNumbers ?? [])],
        }),
      );
      session.current.markNumberInvalid(task, number);
    },
    [dispatch, session],
  );

  const callNextNumber = useCallback(
    async (task: CallTask) => {
      session.current.callNextNumber(task);
    },
    [session],
  );

  const updateContactListQuery = useCallback(
    (query: string) => {
      dispatch(setContactListQuery(query));
    },
    [dispatch],
  );

  const loadOutbound = useCallback(() => {
    loadOnboardingStatus();
    loadCallDispositions();
    dispatch(loadOrgOutboundSettings());
    loadContactLists();
  }, [dispatch, loadCallDispositions, loadContactLists, loadOnboardingStatus]);

  // These effects are run by Outbound and OutboundActiveCall and they
  // can overlap depending on whether the user is in-app. For these two,
  // that should be fine.
  useEffect(() => {
    if (!runEffects) return;
    if (session.current.isOpen()) {
      log.debug('Changing input device.', { inputDevice });
      session.current.setInputDevice(inputDevice);
    }
  }, [inputDevice, runEffects, session]);

  useEffect(() => {
    if (!runEffects) return;
    if (session.current.isOpen()) {
      log.debug('Changing mute state for session.', { muted });
      session.current.mute(muted);
    }
  }, [muted, runEffects, session]);

  return {
    callNextNumber,
    getCallTargets,
    getDebugCallTargets,
    isConnected,
    isDialing,
    isOpen,
    isPaused,
    loadCallDispositions,
    loadContactLists,
    loadMoreData,
    loadOnboardingStatus,
    loadOutbound,
    loadedTwilioAccountStatus,
    markNumberInvalid,
    pauseDialing,
    prepareSession,
    removeServicebellContactList,
    reset,
    skipCall,
    startDialing,
    startDialpadDialing,
    startSingleTaskDialing,
    stopCall,
    stopDialing,
    togglePause,
    toggleSkipped,
    updateContactListQuery,
  };
};

// Returns an async procedure that will load more data based on the
// given cursor and create a new procedure to load the next page of
// data and so on until the pages are exhausted.
//
// The fact that this also dispatches UI state changes is not
// ideal. Instead of keeping separate task lists for the UI and for
// the session, it would be better if the UI could be derived from the
// task list in the OutboundSession object.
const makeLoadMoreData = (
  dispatch: any,
  cursor: number | null,
  sourceId: number | null,
  sourceType: SourceType | undefined,
) => {
  return async () => {
    if (!cursor || !sourceId || !LIST_SOURCE_TYPES.includes(sourceType as SourceType)) {
      return {
        cursor: cursor,
        tasks: [],
        next: makeLoadMoreData(dispatch, cursor, sourceId, sourceType),
      };
    }

    log.info('Loading more call tasks.', { cursor, sourceId, sourceType });
    try {
      const fn =
        sourceType === SourceType.OUTREACH_SEQUENCE
          ? getOutreachContactListCallTasks
          : sourceType == SourceType.HUBSPOT_LIST
          ? getHubspotContactListCallTasks
          : sourceType == SourceType.SERVICEBELL_LIST
          ? getServicebellContactListCallTasks
          : sourceType == SourceType.SALESLOFT_CADENCE
          ? getSalesloftContactListCallTasks
          : null;
      if (!fn) {
        return {
          cursor: cursor,
          tasks: [],
          next: makeLoadMoreData(dispatch, cursor, sourceId, sourceType),
        };
      }
      const {
        data: { cursor: nextCursor, callTasks },
      } = await fn(sourceId, cursor);
      dispatch(addCallTasks(callTasks));
      dispatch(setCursor(nextCursor));
      return {
        cursor: nextCursor,
        tasks: callTasks,
        next: makeLoadMoreData(dispatch, nextCursor, sourceId, sourceType),
      };
    } catch (error) {
      log.error('Failed to load more call tasks.', {
        cursor,
        sourceId,
        sourceType,
        error,
      });
      return {
        cursor: cursor,
        tasks: [],
        next: makeLoadMoreData(dispatch, cursor, sourceId, sourceType),
      };
    }
  };
};
